import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AppLayout from "../../../components/AppLayout/AppLayout";

const itemOptions = ["Microchip", "Stationery", "Travel", "Maintenance", "Office Supplies", "Courier"];
const paymentModes = ["Cash", "UPI", "Bank Transfer", "Card"];

export default function PettyCashExpense() {
  const [activeTab, setActiveTab] = useState("list");
  const [page, setPage] = useState(1);
  const [filterDate, setFilterDate] = useState("");
  const [expenses, setExpenses] = useState([]);
  const [expenseDescription, setExpenseDescription] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const baseAPI = process.env.REACT_APP_API_URL;
  const storedToken = JSON.parse(localStorage.getItem("token"));

  // Add Expense form state
  const [form, setForm] = useState({
    leadId: "",
    item: "",
    amount: "",
    paymentMode: "",
    remark: "",
    receipt: null,
    expenseDate: "",
    expenseType: "",
    expenseCategory: "0",
  });
  const [fileName, setFileName] = useState("");
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    fetchExpenseMasterData();
  }, []);

  const fetchExpenseMasterData = async () => {
    setLoading(true);
    try {
      const headers = {
        Authorization: `Bearer ${storedToken}`,
        "Content-Type": "application/json",
      };
      const [expenseDescriptionRes, balanceSheetRes] = await Promise.all([
        axios.get(
          `${baseAPI}adminGet/GetExpenseDescriptions?expense_type=2&expense_category=0`,
          { headers }
        ),
        axios.get(`${baseAPI}userget/GetPettyCashBalancesheet`, { headers }),
      ]);
      setExpenseDescription(expenseDescriptionRes.data.data || []);
      setExpenses(balanceSheetRes.data.data || []);
    } catch (error) {
      console.error("Error fetching master data:", error);
    } finally {
      setLoading(false);
    }
  };

  const itemsPerPage = 3;
  const filtered = filterDate
    ? expenses.filter((e) => {
        const d = new Date(e.date);
        const [y, m, day] = filterDate.split("-");
        return (
          d.getDate() === parseInt(day) &&
          d.getMonth() + 1 === parseInt(m) &&
          d.getFullYear() === parseInt(y)
        );
      })
    : expenses;
  const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;
  const paginated = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setForm((f) => ({ ...f, receipt: ev.target.result }));
      };
      reader.readAsDataURL(file);
      setFileName(file.name);
    }
  };

  const handleFormChange = (name, value) => {
    setErrors((prev) => ({ ...prev, [name]: "" }));
    let updatedForm = { ...form, [name]: value };

    // Sync expenseCategory when expenseType changes (from API descriptions)
    if (name === "expenseType") {
      const selected = expenseDescription.find(
        (exp) => exp.expense_description_id.toString() === value
      );
      if (selected) {
        updatedForm.expenseCategory = selected.expense_category_id || "0";
        // Also sync the item display name
        updatedForm.item = selected.description || "";
      }
    }
    setForm(updatedForm);
  };

  const validate = () => {
    const e = {};
    if (!form.leadId.trim()) e.leadId = "Lead ID is required";
    if (!form.expenseType && !form.item) e.item = "Please select an item";
    if (!form.amount || isNaN(form.amount) || Number(form.amount) <= 0)
      e.amount = "Enter valid amount";
    if (!form.paymentMode) e.paymentMode = "Select payment mode";
    return e;
  };

  const handleAdd = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) {
      setErrors(e);
      return;
    }

    const payload = {
      Relocation_Id: form.leadId,
      Description: form.remark,
      Expense_Type: form.expenseType || form.item,
      Job_Id: form.leadId || null,
      Document: form.receipt || "",
      expense_date: form.expenseDate,
      expense_amount: form.amount,
      expense_category: form.expenseCategory,
    };

    try {
      await axios.post(
        `${baseAPI}Userpost/AddFieldOpsExpense?ExpenseId=0`,
        payload,
        {
          headers: { Authorization: `Bearer ${storedToken}` },
        }
      );
      setForm({
        leadId: "",
        item: "",
        amount: "",
        paymentMode: "",
        remark: "",
        receipt: null,
        expenseDate: "",
        expenseType: "",
        expenseCategory: "0",
      });
      setFileName("");
      setErrors({});
      if (fileRef.current) fileRef.current.value = "";
      setSubmitted(true);
      fetchExpenseMasterData(); // Refresh list
      setTimeout(() => {
        setSubmitted(false);
        setActiveTab("list");
      }, 1500);
    } catch (error) {
      console.error("Error adding expense:", error);
      alert("Failed to add expense. Please try again.");
    }
  };

  return (
    <AppLayout
      title={activeTab === "list" ? "Expense List" : "Add Expense"}
      onBack={() => navigate(-1)}
    >
      {/* Breadcrumb */}
      <div
        style={{
          padding: "10px 18px 0",
          display: "flex",
          alignItems: "center",
          gap: 6,
          color: "#888",
          fontSize: 13,
        }}
      >
        <svg
          width="14"
          height="14"
          fill="none"
          stroke="#aaa"
          strokeWidth="1.8"
          viewBox="0 0 24 24"
        >
          <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z" />
          <path d="M9 21V12h6v9" />
        </svg>
        <span>Home</span>
        <svg
          width="12"
          height="12"
          fill="none"
          stroke="#bbb"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path d="M9 18l6-6-6-6" />
        </svg>
        <span style={{ color: "#555" }}>Petty Cash</span>
      </div>

      {/* Tabs */}
      <div
        style={{
          padding: "12px 18px 0",
          display: "flex",
          gap: 2,
          borderBottom: "2px solid #e8e8ef",
        }}
      >
        {["list", "add"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: "8px 20px",
              border: "none",
              borderRadius: "6px 6px 0 0",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 13.5,
              transition: "all 0.2s",
              background: activeTab === tab ? "#7c3ab5" : "transparent",
              color: activeTab === tab ? "#fff" : "#888",
            }}
          >
            {tab === "list" ? "Expense List" : "Add Expense"}
          </button>
        ))}
      </div>

      {/* === EXPENSE LIST TAB === */}
      {activeTab === "list" && (
        <div style={{ padding: "16px 16px 20px" }}>
          {/* Title row */}
          <div
            style={{
              background: "#7c3ab5",
              borderRadius: 8,
              padding: "10px 14px",
              marginBottom: 14,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
              Expense List
            </span>
          </div>

          {/* Date filter + Pagination */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 14,
            }}
          >
            <div style={{ position: "relative" }}>
              <input
                type="date"
                value={filterDate}
                onChange={(e) => {
                  setFilterDate(e.target.value);
                  setPage(1);
                }}
                style={{
                  padding: "6px 28px 6px 10px",
                  borderRadius: 7,
                  border: "1.5px solid #ddd",
                  fontSize: 13,
                  color: filterDate ? "#333" : "#aaa",
                  outline: "none",
                  cursor: "pointer",
                  background: "#fafafa",
                }}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  border: "1.5px solid #ddd",
                  background: "#fff",
                  cursor: page === 1 ? "not-allowed" : "pointer",
                  color: "#666",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg
                  width="13"
                  height="13"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  background: "#7c3ab5",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                {page}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  border: "1.5px solid #ddd",
                  background: "#fff",
                  cursor: page === totalPages ? "not-allowed" : "pointer",
                  color: "#666",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg
                  width="13"
                  height="13"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          </div>

          {/* Loading state */}
          {loading && (
            <div
              style={{
                textAlign: "center",
                color: "#9b6dd0",
                padding: "30px 0",
                fontSize: 14,
              }}
            >
              Loading expenses...
            </div>
          )}

          {/* Expense Cards */}
          {!loading && paginated.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                color: "#bbb",
                padding: "30px 0",
                fontSize: 14,
              }}
            >
              No expenses found
            </div>
          ) : (
            !loading &&
            paginated.map((exp, index) => (
              <div
                key={exp.id || index}
                style={{
                  border: "1.5px solid #ede8f8",
                  borderRadius: 10,
                  marginBottom: 12,
                  overflow: "hidden",
                  background: "#fff",
                  boxShadow: "0 2px 8px rgba(120,60,180,0.06)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px 14px",
                    background: "#faf8ff",
                    borderBottom: "1px solid #ede8f8",
                  }}
                >
                  <span style={{ fontSize: 12.5, color: "#999", fontWeight: 500 }}>
                    S No. :{" "}
                    <span style={{ color: "#555" }}>
                      {String(index + 1 + (page - 1) * itemsPerPage).padStart(2, "0")}
                    </span>
                  </span>
                  <span style={{ fontSize: 12.5, color: "#e05c9a", fontWeight: 600 }}>
                    Date : {exp.date}
                  </span>
                </div>
                <div style={{ padding: "10px 14px 12px" }}>
                  <div
                    style={{
                      fontWeight: 700,
                      color: "#7c3ab5",
                      fontSize: 15,
                      marginBottom: 8,
                    }}
                  >
                    {exp.expense_description || exp.item}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {exp.relocation_id && (
                      <div style={{ display: "flex", fontSize: 13, color: "#555" }}>
                        <span style={{ width: 110, color: "#999" }}>Relocation ID</span>
                        <span>: &nbsp;{exp.relocation_id}</span>
                      </div>
                    )}
                    <div style={{ display: "flex", fontSize: 13, color: "#555" }}>
                      <span style={{ width: 110, color: "#999" }}>Lead ID</span>
                      <span>: &nbsp;{exp.lead_id || exp.leadId}</span>
                    </div>
                    <div style={{ display: "flex", fontSize: 13, color: "#555" }}>
                      <span style={{ width: 110, color: "#999" }}>Amount</span>
                      <span>
                        : &nbsp;
                        <span style={{ color: "#333", fontWeight: 600 }}>
                          ₹{" "}
                          {Number(
                            exp.expense_amount || exp.amount || 0
                          ).toLocaleString("en-IN")}
                        </span>
                      </span>
                    </div>
                    {exp.status && (
                      <div style={{ display: "flex", fontSize: 13, color: "#555" }}>
                        <span style={{ width: 110, color: "#999" }}>Status</span>
                        <span>: &nbsp;{exp.status}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* === ADD EXPENSE TAB === */}
      {activeTab === "add" && (
        <div style={{ padding: "18px 18px 24px" }}>
          {submitted && (
            <div
              style={{
                background: "#e8f8f0",
                border: "1.5px solid #5cb87a",
                borderRadius: 8,
                padding: "10px 14px",
                marginBottom: 14,
                color: "#2e7d4f",
                fontWeight: 600,
                fontSize: 13.5,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <svg
                width="16"
                height="16"
                fill="none"
                stroke="#2e7d4f"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
              Expense added successfully!
            </div>
          )}

          {/* Lead ID */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Lead ID</label>
            <input
              value={form.leadId}
              onChange={(e) => handleFormChange("leadId", e.target.value)}
              placeholder="-"
              style={{
                ...inputStyle,
                borderColor: errors.leadId ? "#e05c5c" : "#ddd",
              }}
            />
            {errors.leadId && <span style={errStyle}>{errors.leadId}</span>}
          </div>

          {/* Date of Expense */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Date of Expense</label>
            <input
              type="date"
              value={form.expenseDate}
              onChange={(e) => handleFormChange("expenseDate", e.target.value)}
              style={{ ...inputStyle }}
            />
          </div>

          {/* Item/Description — API-driven if available, fallback to static options */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Item / Description</label>
            <div style={{ position: "relative" }}>
              {expenseDescription.length > 0 ? (
                <select
                  value={form.expenseType}
                  onChange={(e) => handleFormChange("expenseType", e.target.value)}
                  style={{
                    ...inputStyle,
                    appearance: "none",
                    cursor: "pointer",
                    borderColor: errors.item ? "#e05c5c" : "#ddd",
                  }}
                >
                  <option value="">Select</option>
                  {expenseDescription.map((exp) => (
                    <option
                      key={exp.expense_description_id}
                      value={exp.expense_description_id}
                    >
                      {exp.description}
                    </option>
                  ))}
                </select>
              ) : (
                <select
                  value={form.item}
                  onChange={(e) => handleFormChange("item", e.target.value)}
                  style={{
                    ...inputStyle,
                    appearance: "none",
                    cursor: "pointer",
                    borderColor: errors.item ? "#e05c5c" : "#ddd",
                  }}
                >
                  <option value="">Select</option>
                  {itemOptions.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              )}
              <svg
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                }}
                width="14"
                height="14"
                fill="none"
                stroke="#999"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </div>
            {errors.item && <span style={errStyle}>{errors.item}</span>}
          </div>

          {/* Amount + Payment Mode */}
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Amount</label>
              <input
                value={form.amount}
                onChange={(e) => handleFormChange("amount", e.target.value)}
                placeholder="Enter"
                type="number"
                style={{
                  ...inputStyle,
                  borderColor: errors.amount ? "#e05c5c" : "#ddd",
                }}
              />
              {errors.amount && <span style={errStyle}>{errors.amount}</span>}
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Payment Mode</label>
              <div style={{ position: "relative" }}>
                <select
                  value={form.paymentMode}
                  onChange={(e) => handleFormChange("paymentMode", e.target.value)}
                  style={{
                    ...inputStyle,
                    appearance: "none",
                    cursor: "pointer",
                    borderColor: errors.paymentMode ? "#e05c5c" : "#ddd",
                  }}
                >
                  <option value="">Select</option>
                  {paymentModes.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
                <svg
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                  }}
                  width="13"
                  height="13"
                  fill="none"
                  stroke="#999"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </div>
              {errors.paymentMode && (
                <span style={errStyle}>{errors.paymentMode}</span>
              )}
            </div>
          </div>

          {/* Receipt Upload */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Receipt</label>
            <div
              onClick={() => fileRef.current.click()}
              style={{
                border: "1.5px dashed #c0a8e0",
                borderRadius: 8,
                padding: "18px 0",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
                background: "#faf7ff",
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#f3eeff")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "#faf7ff")
              }
            >
              <svg
                width="28"
                height="28"
                fill="none"
                stroke="#9b6dd0"
                strokeWidth="1.6"
                viewBox="0 0 24 24"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M8 7h8M8 11h5" />
                <path d="M15 15l2 2 4-4" strokeWidth="2" />
              </svg>
              <span style={{ fontSize: 13, color: "#9b6dd0" }}>
                {fileName ? (
                  <span style={{ color: "#7c3ab5", fontWeight: 600 }}>
                    {fileName}
                  </span>
                ) : (
                  <>
                    Click to upload <strong>document</strong>
                  </>
                )}
              </span>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </div>

          {/* Remark */}
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>
              Remark{" "}
              <span style={{ color: "#bbb", fontWeight: 400 }}>(If Any)</span>
            </label>
            <textarea
              value={form.remark}
              onChange={(e) => handleFormChange("remark", e.target.value)}
              placeholder="...."
              rows={3}
              style={{ ...inputStyle, resize: "vertical", minHeight: 72, fontFamily: "inherit" }}
            />
          </div>

          {/* ADD Button */}
          <button
            onClick={handleAdd}
            style={{
              width: "100%",
              padding: "13px 0",
              background: "linear-gradient(135deg, #9b2db5 0%, #5b2a8a 100%)",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 15,
              letterSpacing: 1,
              cursor: "pointer",
              boxShadow: "0 4px 16px rgba(100,40,160,0.25)",
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            ADD
          </button>
        </div>
      )}
    </AppLayout>
  );
}

const labelStyle = {
  display: "block",
  fontSize: 12.5,
  color: "#888",
  marginBottom: 5,
  fontWeight: 500,
};
const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "9px 12px",
  borderRadius: 8,
  border: "1.5px solid #ddd",
  fontSize: 13.5,
  color: "#333",
  background: "#fafafa",
  outline: "none",
  transition: "border-color 0.2s",
};
const errStyle = {
  color: "#e05c5c",
  fontSize: 11.5,
  marginTop: 3,
  display: "block",
};
